import { action, computed, makeObservable, observable } from 'mobx';
import { TeamMemberRole } from '@clipr/lib';

import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import Routes from '../../routes';
import { Team } from '../../entities/team';
import {
  GuidedTrainingDomSync,
  getGuidedTrainingCoachPosition,
  getGuidedTrainingStorageKey,
  getGuidedTrainingTargetRect,
  guidedTrainingTargetSelector,
  type GuidedTrainingCoachPosition,
  type GuidedTrainingRect,
} from '../../components/guidedTraining/guidedTrainingCore';

type Phase = 'dashboard' | 'settings' | 'create';

type Step = {
  phase: Phase;
  title: string;
  description: string;
  targetId?: string;
  primaryLabel: string;
};

type AdminGuidedTrainingRect = GuidedTrainingRect;
type CoachPosition = GuidedTrainingCoachPosition;

type OpenPayload = {
  teamId: string;
};

type Progress = {
  teamId: string;
  currentStepIndex: number;
};

const progressStoragePrefix = 'guidedTraining.admin';
const activeBodyClass = 'admin-guided-training-active';
const positionRetryDelayMs = 50;
const positionRetryAttempts = 14;

const getTargetRect = (targetId?: string): AdminGuidedTrainingRect | null =>
  getGuidedTrainingTargetRect(targetId);

const getCoachPosition = (rect: AdminGuidedTrainingRect | null): CoachPosition =>
  getGuidedTrainingCoachPosition(rect, {
    maxWidth: 420,
    height: 220,
    defaultLeft: 'right',
    defaultTopRatio: 0.35,
    defaultTopMin: 120,
  });

export class AdminGuidedTrainingState
  extends StoreNode {

  readonly nodeType = 'AdminGuidedTraining';

  constructor(store: Store) {
    super(store);
    makeObservable(this);
    this.domSync = new GuidedTrainingDomSync({
      syncPosition: this.syncPosition,
      activeBodyClass,
      onKeyDown: this.handleKeyDown,
      retryAttempts: positionRetryAttempts,
      retryDelayMs: positionRetryDelayMs,
    });
  }

  @observable isArmed = false;
  @observable isVisible = false;
  @observable currentStepIndex = 0;
  @observable teamId: string | null = null;
  @observable.ref targetRect: AdminGuidedTrainingRect | null = null;
  @observable.ref coachPosition: CoachPosition = { top: 120, left: 16, width: 420 };

  private readonly domSync: GuidedTrainingDomSync;

  @computed
  get steps(): Step[] {
    return [
      {
        phase: 'dashboard',
        title: 'Open the Gear menu',
        description: 'This training is available only to CLIPr Admins and their right hand. Open the gear menu for the selected Library.',
        targetId: 'admin-training-gear',
        primaryLabel: 'Next',
      },
      {
        phase: 'dashboard',
        title: 'Choose Library settings',
        description: 'Choose Library settings to manage Library defaults, access, members, and invitations.',
        targetId: 'admin-training-gear-menu',
        primaryLabel: 'Next',
      },
      {
        phase: 'settings',
        title: 'Invite Library members',
        description: 'The Members tab shows everyone with access. Select Invite to add an administrator or member.',
        targetId: 'admin-training-invite-button',
        primaryLabel: 'Next',
      },
      {
        phase: 'settings',
        title: 'Send the invitation',
        description: 'Enter the email address, choose Admin or Member, and send the invitation. CSV upload remains available for bulk invitations.',
        targetId: 'admin-training-invite-window',
        primaryLabel: 'Next',
      },
      {
        phase: 'dashboard',
        title: 'Create a custom report format',
        description: 'Select Create to teach CLIPr a new agency-specific report format using an approved sample report. Skip if you are not ready to create a reusable template.',
        targetId: 'team-train',
        primaryLabel: 'Open Create',
      },
      {
        phase: 'create',
        title: 'Name the report format',
        description: 'Choose a clear name and describe exactly what the report should capture. These instructions guide how CLIPr generates reports when you use your custom template.',
        targetId: 'admin-training-create-fields',
        primaryLabel: 'Next',
      },
      {
        phase: 'create',
        title: 'Upload a representative sample',
        description: 'Drag and drop a completed sample report, or click the upload area to select one manually. CLIPr learns the structure, not the case-specific facts.',
        targetId: 'admin-training-create-upload',
        primaryLabel: 'Next',
      },
      {
        phase: 'create',
        title: 'Review and activate',
        description: 'Review the extracted structure and generated instructions. Save a draft for later, or activate the format so it becomes available as a Job Specialty.',
        targetId: 'admin-training-create-preview',
        primaryLabel: 'Next',
      },
      {
        phase: 'settings',
        title: 'Set the default Job Specialty',
        description: 'When a custom format is ready, it appears in Library Settings and can be selected as the default Job Specialty for future uploads.',
        targetId: 'admin-training-default-job-speciality',
        primaryLabel: 'Complete Admin Training',
      },
    ];
  }

  @computed
  get currentStep(): Step | null {
    return this.steps[this.currentStepIndex] || null;
  }

  @computed
  get totalSteps(): number {
    return this.steps.length;
  }

  @computed
  get showBackButton(): boolean {
    return this.currentStepIndex > 0;
  }

  private get progressStorageKey(): string {
    return getGuidedTrainingStorageKey(this.store, progressStoragePrefix);
  }

  private get completedStorageKey(): string {
    return getGuidedTrainingStorageKey(this.store, progressStoragePrefix, 'completed');
  }

  get hasCompleted(): boolean {
    return !!this.store.storage.getLocal(this.completedStorageKey, true);
  }

  canShowForTeam(teamCanEdit: boolean, teamOrId?: Team | string | null): boolean {
    return teamCanEdit && this.isEligibleForTeam(teamOrId);
  }

  canStart(teamCanEdit: boolean, teamOrId?: Team | string | null): boolean {
    return this.canShowForTeam(teamCanEdit, teamOrId) && !this.hasCompleted;
  }

  @action
  open(payload: OpenPayload, teamCanEdit = true, team?: Team | null) {
    if (!this.canStart(teamCanEdit, team ?? payload.teamId)) return;

    this.clearProgress();
    this.resetRuntime();
    this.teamId = payload.teamId;
    this.currentStepIndex = 0;
    this.isArmed = true;
    this.isVisible = true;
    this.saveProgress();
    this.domSync.bindListeners();
    this.domSync.schedulePositionSync(true);
  }

  @action
  openCreate(payload: OpenPayload, teamCanEdit = true, team?: Team | null): boolean {
    if (!this.canStart(teamCanEdit, team ?? payload.teamId)) return false;

    this.clearProgress();
    this.resetRuntime();
    this.teamId = payload.teamId;
    this.currentStepIndex = 5;
    this.isArmed = true;
    this.isVisible = true;
    this.saveProgress();
    this.syncCreatePageStep();
    this.domSync.bindListeners();
    this.domSync.schedulePositionSync(true);
    return true;
  }

  @action
  handleTeamDashboardMounted(teamId: string | null): boolean {
    const restored = !this.isArmed && this.restoreProgress();
    if (!this.isArmed && !restored) return false;

    if (teamId)
      this.teamId = teamId;

    if (!this.canContinueForTeam(this.teamId)) {
      this.clearProgress();
      this.resetRuntime();
      return false;
    }

    if (this.currentStep?.phase !== 'dashboard') {
      this.navigateToStepPhase();
      return true;
    }

    this.isVisible = true;
    this.domSync.bindListeners();
    this.domSync.schedulePositionSync(true);
    return true;
  }

  @action
  handleTeamDashboardUnmounted(teamId: string | null) {
    if (!this.isArmed || this.teamId !== teamId) return;
    this.pause();
  }

  @action
  handleTeamSettingsMounted(teamId: string | null): boolean {
    const restored = !this.isArmed && this.restoreProgress();
    if (!this.isArmed && !restored) return false;

    if (teamId)
      this.teamId = teamId;

    if (!this.canContinueForTeam(this.teamId)) {
      this.clearProgress();
      this.resetRuntime();
      return false;
    }

    if (this.currentStepIndex === 1) {
      this.currentStepIndex = 2;
      this.saveProgress();
    }

    if (this.currentStep?.phase !== 'settings') {
      this.navigateToStepPhase();
      return true;
    }

    if (this.currentStepIndex === 3 && !this.store.teamMembersWindow.window.isVisible)
      void this.store.teamMembersWindow.openTeamEdit(this.teamId || '', 'invitations');

    this.isVisible = true;
    this.domSync.bindListeners();
    this.domSync.schedulePositionSync(true);
    return true;
  }

  @action
  handleTeamSettingsUnmounted(teamId: string | null) {
    if (!this.isArmed || this.teamId !== teamId) return;
    this.pause();
  }

  @action
  handleCreatePageMounted(teamId: string | null): boolean {
    const restored = !this.isArmed && this.restoreProgress();
    if (!this.isArmed && !restored) return false;

    if (teamId)
      this.teamId = teamId;

    if (!this.canContinueForTeam(this.teamId)) {
      this.clearProgress();
      this.resetRuntime();
      return false;
    }

    if (this.currentStepIndex === 4) {
      this.currentStepIndex = 5;
      this.saveProgress();
    }

    if (this.currentStep?.phase !== 'create') {
      this.navigateToStepPhase();
      return true;
    }

    this.syncCreatePageStep();
    this.isVisible = true;
    this.domSync.bindListeners();
    this.domSync.schedulePositionSync(true);
    return true;
  }

  @action
  handleCreatePageUnmounted(teamId: string | null) {
    if (!this.isArmed || this.teamId !== teamId) return;
    this.pause();
  }

  @action
  handleGearOpened(teamId = this.teamId, teamCanEdit = true, team?: Team | null) {
    if (!this.isArmed) {
      if (!teamId || !this.canStart(teamCanEdit, team ?? teamId)) return;

      this.clearProgress();
      this.resetRuntime();
      this.teamId = teamId;
      this.currentStepIndex = 1;
      this.isArmed = true;
      this.isVisible = true;
      this.saveProgress();
      this.domSync.bindListeners();
      this.domSync.schedulePositionSync(true);
      return;
    }

    if (this.currentStepIndex !== 0) return;

    this.currentStepIndex = 1;
    this.saveProgress();
    this.domSync.schedulePositionSync(true);
  }

  @action
  handleLibrarySettingsSelected() {
    if (!this.isArmed || this.currentStepIndex !== 1) return;

    this.currentStepIndex = 2;
    this.saveProgress();
    this.isVisible = false;
    this.domSync.cancelPositionSync();
  }

  @action
  handleInviteOpened() {
    if (!this.isArmed || this.currentStepIndex !== 2) return;

    this.currentStepIndex = 3;
    this.saveProgress();
    this.domSync.schedulePositionSync(true);
  }

  @action
  handleCreateContextSubmitted() {
    if (!this.isArmed || this.currentStepIndex !== 5) return;

    this.currentStepIndex = 6;
    this.saveProgress();
    this.domSync.schedulePositionSync(true);
  }

  @action
  handleCreatePreviewReady() {
    if (!this.isArmed || this.currentStepIndex !== 6) return;

    this.currentStepIndex = 7;
    this.saveProgress();
    this.domSync.schedulePositionSync(true);
  }

  @action
  handleCreateActivated() {
    if (!this.isArmed || this.currentStepIndex !== 7) return;

    this.currentStepIndex = 8;
    this.saveProgress();
    this.isVisible = false;
    this.domSync.cancelPositionSync();
  }

  @action
  next = () => {
    switch (this.currentStepIndex) {
      case 0:
        this.openGearMenu();
        return;
      case 1:
        this.handleLibrarySettingsSelected();
        this.goToSettingsPage();
        return;
      case 2:
        this.openInviteWindow();
        return;
      case 3:
        this.store.teamMembersWindow.close();
        this.currentStepIndex = 4;
        this.saveProgress();
        this.goToDashboard();
        return;
      case 4:
        this.currentStepIndex = 5;
        this.saveProgress();
        this.goToCreatePage();
        return;
      case 5:
        this.advanceCreateTourStep(6);
        return;
      case 6:
        this.advanceCreateTourStep(7);
        return;
      case 7:
        this.currentStepIndex = 8;
        this.saveProgress();
        this.isVisible = false;
        this.domSync.cancelPositionSync();
        this.goToSettingsPage();
        return;
      case 8:
        this.complete();
        return;
      default:
        break;
    }
  }

  @action
  back = () => {
    if (!this.showBackButton) return;

    if (this.currentStepIndex === 3) {
      this.store.teamMembersWindow.close();
      this.currentStepIndex = 2;
    } else {
      this.currentStepIndex = Math.max(0, this.currentStepIndex - 1);
    }

    this.saveProgress();
    this.navigateToStepPhase();
    this.domSync.schedulePositionSync(true);
  }

  @action
  syncPosition = () => {
    const rect = getTargetRect(this.currentStep?.targetId);
    this.targetRect = rect;
    this.coachPosition = getCoachPosition(rect);

    if (!rect && this.currentStep?.targetId && this.domSync.canRetry)
      this.domSync.schedulePositionRetry();
  }

  @action
  pause = () => {
    if (this.isArmed)
      this.saveProgress();

    this.isVisible = false;
    this.targetRect = null;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
  }

  @action
  skip = () => {
    const exitTeamId = this.teamId;
    const phase = this.currentStep?.phase;

    this.store.teamMembersWindow.close();
    this.complete();

    if (phase === 'create' && exitTeamId)
      this.store.goTo(Routes.teamDashboard(exitTeamId));
  }

  @action
  private complete() {
    this.store.storage.setLocal(this.completedStorageKey, true, true);
    this.clearProgress();
    this.resetRuntime();
  }

  @action
  private resetRuntime() {
    this.isArmed = false;
    this.isVisible = false;
    this.currentStepIndex = 0;
    this.teamId = null;
    this.targetRect = null;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
  }

  private openGearMenu() {
    const button = document.querySelector<HTMLElement>(guidedTrainingTargetSelector('admin-training-gear'));
    button?.click();

    if (!button)
      this.domSync.schedulePositionSync(true);
  }

  private openInviteWindow() {
    this.store.teamSettingsPage.openTeamInviteWindow();
  }

  private advanceCreateTourStep(stepIndex: number) {
    this.currentStepIndex = stepIndex;
    this.saveProgress();
    this.syncCreatePageStep();
    this.domSync.schedulePositionSync(true);
  }

  private syncCreatePageStep() {
    if (this.currentStepIndex === 6)
      this.store.trainPage.goToStep('upload');
    if (this.currentStepIndex === 7)
      this.store.trainPage.goToStep('preview');
  }

  private navigateToStepPhase() {
    switch (this.currentStep?.phase) {
      case 'dashboard':
        this.goToDashboard();
        break;
      case 'settings':
        this.goToSettingsPage();
        break;
      case 'create':
        this.goToCreatePage();
        break;
    }
  }

  private goToDashboard() {
    if (this.teamId)
      this.store.goTo(Routes.teamDashboard(this.teamId));
  }

  private goToSettingsPage() {
    if (this.teamId)
      this.store.goTo(Routes.teamSettings(this.teamId));
  }

  private goToCreatePage() {
    if (this.teamId)
      this.store.goTo(Routes.teamTrain(this.teamId));
  }

  private saveProgress() {
    if (!this.teamId) return;

    this.store.storage.setLocal(this.progressStorageKey, {
      teamId: this.teamId,
      currentStepIndex: this.currentStepIndex,
    }, true);
  }

  private restoreProgress(): boolean {
    if (this.hasCompleted)
      return false;

    const progress = this.store.storage.getLocal<Progress>(this.progressStorageKey, true);
    if (!progress?.teamId) return false;

    this.teamId = progress.teamId;
    this.currentStepIndex = clamp(progress.currentStepIndex, 0, this.steps.length - 1);
    this.isArmed = true;
    return true;
  }

  private isEligibleForTeam(teamOrId?: Team | string | null): boolean {
    const user = this.store.user;
    if (!user)
      return false;

    const team = this.resolveTeam(teamOrId);
    const role = team?.currentUserMember?.role;
    const ownerEmail = team?.ownerEmail?.toLowerCase();
    const userEmail = user.email?.toLowerCase();
    const hasLeadershipTitle = /\b(chief|sheriff)\b/i.test(user.jobTitle ?? '');
    const isAgencyAdmin = team?.publicSafetyActivated === true && role === TeamMemberRole.Admin;
    const isLibraryOwner = role === TeamMemberRole.Owner || (!!ownerEmail && ownerEmail === userEmail);

    return user.isAgencySponsor || isAgencyAdmin || hasLeadershipTitle || isLibraryOwner;
  }

  private canContinueForTeam(teamId: string | null): boolean {
    const team = this.resolveTeam(teamId);
    if (!team)
      return this.isEligibleForTeam(teamId);

    return this.canShowForTeam(team.hasPermission('EditTeam'), team);
  }

  private resolveTeam(teamOrId?: Team | string | null): Team | null {
    if (!teamOrId)
      return null;

    if (typeof teamOrId !== 'string')
      return teamOrId;

    return this.store.teamManager.getTeam(teamOrId);
  }

  private clearProgress() {
    this.store.storage.removeLocal(this.progressStorageKey);
  }

  private handleKeyDown = (evt: KeyboardEvent) => {
    if (evt.key === 'Escape')
      this.pause();
  }
}
